import { HermesError } from "./errors";

/** 广播：多个订阅者各自收到同样的值（对应 Swift 的 Broadcaster）。 */
export class Broadcaster<T> {
  private listeners = new Set<(value: T) => void>();
  private latest: { value: T } | undefined;

  constructor(private readonly replayLatest = false) {}

  get subscriberCount(): number { return this.listeners.size; }

  subscribe(listener: (value: T) => void): () => void {
    this.listeners.add(listener);
    if (this.replayLatest && this.latest) listener(this.latest.value);
    return () => { this.listeners.delete(listener); };
  }

  yield(value: T): void {
    if (this.replayLatest) this.latest = { value };
    for (const l of [...this.listeners]) {
      try { l(value); } catch (e) { console.error(e); }
    }
  }
}

/** 先进先出的异步队列：生产者 push，消费者 await next()；关闭后 next() 抛出关闭原因。 */
export class AsyncQueue<T> {
  private buffer: T[] = [];
  private waiters: { resolve: (v: T) => void; reject: (e: unknown) => void }[] = [];
  private closedError: unknown;
  private closed = false;

  push(value: T): void {
    if (this.closed) return;
    const w = this.waiters.shift();
    if (w) w.resolve(value); else this.buffer.push(value);
  }

  next(): Promise<T> {
    if (this.buffer.length > 0) return Promise.resolve(this.buffer.shift() as T);
    if (this.closed) return Promise.reject(this.closedError);
    return new Promise((resolve, reject) => this.waiters.push({ resolve, reject }));
  }

  close(error: unknown): void {
    if (this.closed) return;
    this.closed = true;
    this.closedError = error;
    const ws = this.waiters;
    this.waiters = [];
    for (const w of ws) w.reject(error);
  }

  get isClosed(): boolean { return this.closed; }
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(HermesError.cancelled()); return; }
    const t = setTimeout(() => { signal?.removeEventListener("abort", onAbort); resolve(); }, ms);
    const onAbort = () => { clearTimeout(t); reject(HermesError.cancelled()); };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** 在期限内完成，否则调用 onTimeout 并抛出 err */
export async function withDeadline<T>(ms: number, body: Promise<T>, onTimeout: () => void, err: () => Error): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => { onTimeout(); reject(err()); }, ms);
  });
  try {
    return await Promise.race([body, timeout]);
  } finally {
    clearTimeout(timer);
  }
}
